import { useState, useEffect, useCallback, useRef } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  BuildingOffice2Icon,
  DevicePhoneMobileIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Input, Spinner } from '../../../components';
import {
  useWhatsAppStatus,
  useConfigureWhatsApp,
  useEmbeddedSignupCallback,
  useConnectBaileys,
  useBaileysStatus,
  useDisconnectBaileys,
} from '../hooks/useSettings';
import { cn } from '../../../lib/utils';
import WhatsAppNumbersList from './WhatsAppNumbersList';

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

const META_APP_ID = import.meta.env.VITE_META_APP_ID || '';
const META_CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID || '';

type ConnectionTab = 'business' | 'personal' | 'manual';

export default function WhatsAppSettings() {
  const { data: status, isLoading } = useWhatsAppStatus();
  const configureWhatsApp = useConfigureWhatsApp();
  const embeddedSignup = useEmbeddedSignupCallback();
  const connectBaileys = useConnectBaileys();
  const disconnectBaileys = useDisconnectBaileys();

  const [activeTab, setActiveTab] = useState<ConnectionTab>('business');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fbReady, setFbReady] = useState(false);

  // Baileys QR state
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const { data: baileysStatus } = useBaileysStatus(isPolling);

  // Manual form state
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [businessAccountId, setBusinessAccountId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [webhookVerifyToken, setWebhookVerifyToken] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Several numbers per store: a label for the number being connected, and
  // whether the admin asked to add another one.
  const [numberLabel, setNumberLabel] = useState('');
  const [addingNumber, setAddingNumber] = useState(false);
  const optionsRef = useRef<HTMLDivElement>(null);
  const numbers = status?.numbers ?? [];
  const hasCloudNumber = status?.configured && status?.provider !== 'baileys' && numbers.length > 0;
  const multiNumber = numbers.length > 1;
  const showLabelField = hasCloudNumber && (addingNumber || multiNumber);

  // Embedded signup session info ref
  const sessionInfoRef = useRef<{ wabaId: string; phoneNumberId: string } | null>(null);

  // Init Facebook SDK
  useEffect(() => {
    if (!META_APP_ID) return;

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v24.0',
      });
      setFbReady(true);
    };

    // If FB SDK already loaded
    if (window.FB) {
      window.FB.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v24.0',
      });
      setFbReady(true);
    }
  }, []);

  // Listen for Embedded Signup session info
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      )
        return;

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA') {
            sessionInfoRef.current = {
              wabaId: data.data.waba_id,
              phoneNumberId: data.data.phone_number_id,
            };
          }
        }
      } catch {
        // not our message
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Update QR code from polling, stop polling when connected
  useEffect(() => {
    if (baileysStatus?.connected) {
      setIsPolling(false);
      setQrCode(null);
    } else if (baileysStatus?.qrCode) {
      setQrCode(baileysStatus.qrCode);
    }
  }, [baileysStatus?.connected, baileysStatus?.qrCode]);

  const handleEmbeddedSignup = useCallback(() => {
    if (!window.FB || !fbReady) return;

    window.FB.login(
      (response: any) => {
        if (response.authResponse) {
          const code = response.authResponse.code;
          const info = sessionInfoRef.current;
          if (code && info) {
            embeddedSignup.mutate(
              {
                code,
                waba_id: info.wabaId,
                phone_number_id: info.phoneNumberId,
                label: numberLabel.trim() || undefined,
              },
              {
                onSuccess: () => {
                  setNumberLabel('');
                  setAddingNumber(false);
                },
              }
            );
          }
        }
      },
      {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
        },
      }
    );
  }, [fbReady, embeddedSignup, numberLabel]);

  const handleConnectBaileys = useCallback(() => {
    connectBaileys.mutate(undefined, {
      onSuccess: (data) => {
        setQrCode(data.qrCode);
        setIsPolling(true);
      },
    });
  }, [connectBaileys]);

  function validateManual(): boolean {
    const newErrors: Record<string, string> = {};
    if (!phoneNumberId.trim()) newErrors.phoneNumberId = 'Required';
    if (!businessAccountId.trim()) newErrors.businessAccountId = 'Required';
    if (!accessToken.trim()) newErrors.accessToken = 'Required';
    if (!appSecret.trim()) newErrors.appSecret = 'Required';
    if (!webhookVerifyToken.trim()) newErrors.webhookVerifyToken = 'Required';
    if (!whatsappNumber.trim()) newErrors.whatsappNumber = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateManual()) return;

    configureWhatsApp.mutate(
      {
        phone_number_id: phoneNumberId,
        business_account_id: businessAccountId,
        access_token: accessToken,
        app_secret: appSecret,
        webhook_verify_token: webhookVerifyToken,
        whatsapp_number: whatsappNumber,
        label: numberLabel.trim() || undefined,
      },
      {
        onSuccess: () => {
          setShowAdvanced(false);
          setAccessToken('');
          setAppSecret('');
          setNumberLabel('');
          setAddingNumber(false);
        },
      }
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner text="Loading WhatsApp status..." />
      </div>
    );
  }

  const providerLabel =
    status?.provider === 'baileys'
      ? 'Personal WhatsApp (QR)'
      : status?.provider === 'cloud_api'
        ? 'WhatsApp Business API'
        : null;

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand/10">
              <svg className="h-6 w-6 text-brand" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-ink">WhatsApp</h3>
              <p className="text-sm text-ink-2">
                Connect your WhatsApp to receive and send messages
              </p>
            </div>
          </div>
          {status?.configured ? (
            <div className="flex items-center gap-2">
              {providerLabel && (
                <span className="text-xs text-ink-3">{providerLabel}</span>
              )}
              <Badge variant="success" size="md">
                <CheckCircleIcon className="mr-1 h-4 w-4" />
                Connected
              </Badge>
            </div>
          ) : (
            <Badge variant="danger" size="md">
              <XCircleIcon className="mr-1 h-4 w-4" />
              Not Connected
            </Badge>
          )}
        </div>

        {status?.configured && multiNumber && (
          <div className="mt-4">
            <WhatsAppNumbersList numbers={numbers} />
          </div>
        )}

        {status?.configured && !multiNumber && (
          <div className="mt-4 rounded-card bg-surface-2 p-4">
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {status.whatsappNumber && (
                <div>
                  <dt className="text-xs font-medium text-ink-3">Phone Number</dt>
                  <dd className="text-sm text-ink">{status.whatsappNumber}</dd>
                </div>
              )}
              {status.phoneNumberId && (
                <div>
                  <dt className="text-xs font-medium text-ink-3">Phone Number ID</dt>
                  <dd className="text-sm text-ink">{status.phoneNumberId}</dd>
                </div>
              )}
              {status.businessAccountId && (
                <div>
                  <dt className="text-xs font-medium text-ink-3">Business Account ID</dt>
                  <dd className="text-sm text-ink">{status.businessAccountId}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {hasCloudNumber && (
          <div className="mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAddingNumber(true);
                setActiveTab('business');
                optionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              Add another number
            </Button>
          </div>
        )}
      </div>

      {/* Connection Options */}
      <div ref={optionsRef} className="rounded-panel border border-line bg-surface shadow-s">
        {/* Tab navigation */}
        <div className="border-b border-line px-6">
          <nav className="-mb-px flex gap-6">
            <button
              onClick={() => setActiveTab('business')}
              className={cn(
                'flex items-center gap-2 border-b-2 py-3 text-sm font-medium transition-colors',
                activeTab === 'business'
                  ? 'border-brand text-brand'
                  : 'border-transparent text-ink-3 hover:border-line-2 hover:text-ink'
              )}
            >
              <BuildingOffice2Icon className="h-4 w-4" />
              Business API
            </button>
            <button
              onClick={() => setActiveTab('personal')}
              className={cn(
                'flex items-center gap-2 border-b-2 py-3 text-sm font-medium transition-colors',
                activeTab === 'personal'
                  ? 'border-brand text-brand'
                  : 'border-transparent text-ink-3 hover:border-line-2 hover:text-ink'
              )}
            >
              <DevicePhoneMobileIcon className="h-4 w-4" />
              Personal WhatsApp
            </button>
            <button
              onClick={() => setActiveTab('manual')}
              className={cn(
                'flex items-center gap-2 border-b-2 py-3 text-sm font-medium transition-colors',
                activeTab === 'manual'
                  ? 'border-brand text-brand'
                  : 'border-transparent text-ink-3 hover:border-line-2 hover:text-ink'
              )}
            >
              <WrenchScrewdriverIcon className="h-4 w-4" />
              Manual Setup
            </button>
          </nav>
        </div>

        <div className="p-6">
          {showLabelField && activeTab !== 'personal' && (
            <div className="mb-6 space-y-3 rounded-card border border-line bg-surface-2 p-4">
              <p className="text-sm text-ink-2">
                Connecting a number adds it alongside the ones you already have; it does not
                replace them. Messages to every number arrive in this inbox.
              </p>
              <Input
                label="Label for this number"
                value={numberLabel}
                onChange={(e) => setNumberLabel(e.target.value)}
                placeholder="e.g. Abuja"
                maxLength={100}
                helperText="Shown on each conversation so staff know which line the customer wrote to"
              />
            </div>
          )}

          {/* Business API Tab — Embedded Signup */}
          {activeTab === 'business' && (
            <div className="space-y-4">
              <div>
                <h4 className="font-display text-base font-semibold text-ink">
                  WhatsApp Business API
                </h4>
                <p className="mt-1 text-sm text-ink-2">
                  Connect with your Meta Business account. Best for businesses that need
                  official WhatsApp Business API features like verified profiles, templates,
                  and high message volumes.
                </p>
              </div>

              <div className="rounded-card border border-brand/20 bg-brand/10 p-4">
                <h5 className="text-sm font-medium text-brand">What you need</h5>
                <ul className="mt-2 space-y-1 text-sm text-ink-2">
                  <li>A Facebook / Meta account</li>
                  <li>A phone number not already registered on WhatsApp</li>
                  <li>A business name for verification</li>
                </ul>
              </div>

              {META_APP_ID && META_CONFIG_ID ? (
                <Button
                  onClick={handleEmbeddedSignup}
                  isLoading={embeddedSignup.isPending}
                  disabled={!fbReady}
                  className="inline-flex items-center gap-2"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Connect with Facebook
                </Button>
              ) : (
                <div className="rounded-card border border-warning/20 bg-warning/10 p-4 text-sm text-warning">
                  Meta Embedded Signup is not configured. Set{' '}
                  <code className="rounded bg-warning/15 px-1">VITE_META_APP_ID</code> and{' '}
                  <code className="rounded bg-warning/15 px-1">VITE_META_CONFIG_ID</code>{' '}
                  in your environment, or use Manual Setup.
                </div>
              )}
            </div>
          )}

          {/* Personal WhatsApp Tab — Baileys QR */}
          {activeTab === 'personal' && (
            <div className="space-y-4">
              <div>
                <h4 className="font-display text-base font-semibold text-ink">
                  Personal WhatsApp
                </h4>
                <p className="mt-1 text-sm text-ink-2">
                  Connect your existing WhatsApp number by scanning a QR code — no Meta
                  Business account needed. Best for small businesses getting started.
                </p>
              </div>

              <div className="rounded-card border border-warning/20 bg-warning/10 p-4">
                <h5 className="text-sm font-medium text-warning">Important</h5>
                <ul className="mt-2 space-y-1 text-sm text-ink-2">
                  <li>Your phone must stay connected to the internet</li>
                  <li>Uses WhatsApp Web protocol (unofficial API)</li>
                  <li>Message volume limits are lower than Business API</li>
                </ul>
              </div>

              {qrCode ? (
                <div className="space-y-3">
                  <p className="text-sm text-ink-2">
                    Scan this QR code with WhatsApp on your phone:
                  </p>
                  <div className="inline-block rounded-card border border-line bg-surface p-4">
                    <img
                      src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                      alt="WhatsApp QR Code"
                      className="h-64 w-64"
                    />
                  </div>
                  {isPolling && (
                    <p className="flex items-center gap-2 text-sm text-ink-3">
                      <Spinner size="sm" />
                      Waiting for scan...
                    </p>
                  )}
                </div>
              ) : status?.provider === 'baileys' && status.configured ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-success">
                    <CheckCircleIcon className="h-5 w-5" />
                    Connected via Personal WhatsApp
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => disconnectBaileys.mutate()}
                    isLoading={disconnectBaileys.isPending}
                  >
                    Disconnect
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleConnectBaileys}
                  isLoading={connectBaileys.isPending}
                >
                  Connect via QR Code
                </Button>
              )}
            </div>
          )}

          {/* Manual Setup Tab */}
          {activeTab === 'manual' && (
            <div className="space-y-4">
              <div>
                <h4 className="font-display text-base font-semibold text-ink">Manual Setup</h4>
                <p className="text-sm text-ink-2">
                  Enter your WhatsApp Business API credentials manually from the{' '}
                  <a
                    href="https://developers.facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand hover:underline"
                  >
                    Meta Developer Portal
                  </a>
                  .
                </p>
              </div>

              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    label="WhatsApp Phone Number"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    placeholder="+1 234 567 8900"
                    error={errors.whatsappNumber}
                    helperText="International format with country code"
                  />
                  <Input
                    label="Phone Number ID"
                    value={phoneNumberId}
                    onChange={(e) => setPhoneNumberId(e.target.value)}
                    placeholder="e.g. 123456789012345"
                    error={errors.phoneNumberId}
                  />
                  <Input
                    label="Business Account ID"
                    value={businessAccountId}
                    onChange={(e) => setBusinessAccountId(e.target.value)}
                    placeholder="e.g. 123456789012345"
                    error={errors.businessAccountId}
                  />
                  <Input
                    label="Webhook Verify Token"
                    value={webhookVerifyToken}
                    onChange={(e) => setWebhookVerifyToken(e.target.value)}
                    placeholder="Choose a secure verify token"
                    error={errors.webhookVerifyToken}
                    helperText="You'll enter this in Meta's webhook settings"
                  />
                </div>

                <Input
                  label="Access Token"
                  type="password"
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  placeholder="WhatsApp API access token"
                  error={errors.accessToken}
                />

                <Input
                  label="App Secret"
                  type="password"
                  value={appSecret}
                  onChange={(e) => setAppSecret(e.target.value)}
                  placeholder="WhatsApp app secret"
                  error={errors.appSecret}
                  helperText="Used for webhook signature verification"
                />

                <div className="flex items-center gap-3 pt-2">
                  <Button type="submit" isLoading={configureWhatsApp.isPending}>
                    Save Configuration
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
